import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Lock, User, Eye, EyeOff, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { loginUser, clearError } from '../store/slices/authSlice';
import styles from './Login.module.css';

const Login = () => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const validateForm = () => {
    if (!username.trim()) {
      setLocalError('Please enter your username');
      return false;
    }
    if (username.length < 4) {
      setLocalError('Username must be at least 4 characters');
      return false;
    }
    if (!password.trim()) {
      setLocalError('Please enter your password');
      return false;
    }
    if (password.length < 4) {
      setLocalError('Password must be at least 4 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    dispatch(clearError());

    if (!validateForm()) return;

    dispatch(loginUser({ username: username.toUpperCase(), password }));
    setLocalError('');
  };

  const displayError = localError || error;

  return (
    <div className={styles.container}>
      <div className={styles.loginCard}>
        {/* Brand */}
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">R</span>
          <span className={styles.brandName}>Relabix</span>
        </div>

        <div className={styles.heading}>
          <h1 className={styles.title}>Sign in</h1>
          <p className={styles.subtitle}>Use your user code and password to continue</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="login-username">Username</label>
            <div className={styles.inputWrapper}>
              <User className={styles.inputIcon} size={16} aria-hidden="true" />
              <input
                id="login-username"
                type="text"
                className={styles.input}
                placeholder="Enter your username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.toUpperCase());
                  setLocalError('');
                  dispatch(clearError());
                }}
                disabled={loading}
                autoComplete="username"
                autoCapitalize="characters"
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="login-password">Password</label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} size={16} aria-hidden="true" />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className={`${styles.input} ${styles.inputWithAction}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setLocalError('');
                  dispatch(clearError());
                }}
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {displayError && (
            <div className={styles.errorMessage} role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{displayError}</span>
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className={styles.spinner} size={16} />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <LogIn size={16} />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className={styles.footer}>
          <p>Powered by <strong>Relabix</strong></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
